import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'theme';

import SnackbarContext from 'contexts/snackbarContext';

type ResetPopupProps = {
  open: boolean;
  score: number | null;
  onConfirm: () => void | Promise<void>;
  handleClose: () => void;
};

const ResetPopup = ({
  open,
  score,
  onConfirm,
  handleClose,
}: ResetPopupProps) => {
  const { t } = useTranslation();
  const { handleOpen } = useContext(SnackbarContext);

  const [isOpen, setIsOpen] = useState(open);
  const [confirmText, setConfirmText] = useState(false);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const onClose = () => {
    handleClose();
    setTimeout(() => {
      setConfirmText(false);
    }, 200);
  };

  const resetHandler = async () => {
    if (confirmText) {
      await onConfirm();
      handleOpen(t('resetScorePopup.resetSuccessMessage'), 'success');
      setConfirmText(false);
      handleClose();
    } else {
      setConfirmText(true);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          padding: '20px',
          width: '540px',
        },
      }}
    >
      <DialogTitle sx={{ padding: 0 }}>
        {t('resetScorePopup.title')}
      </DialogTitle>

      <Grid
        container
        spacing={2}
        sx={{ justifyContent: 'space-between', padding: '20px 0px' }}
      >
        <Grid item>
          <Typography>{t('resetScorePopup.currentScore')}</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {score ?? 0}
          </Typography>
        </Grid>
      </Grid>

      {confirmText && (
        <Typography color={theme.palette.primary.main}>
          {t('resetScorePopup.confirmReset')}
        </Typography>
      )}

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('resetScorePopup.cancel')}
        </Button>

        {confirmText ? (
          <Button
            onClick={resetHandler}
            variant="contained"
            color="error"
            startIcon={<RefreshIcon />}
          >
            {t('formFields.submitButtonLabel')}
          </Button>
        ) : (
          <Button
            onClick={resetHandler}
            variant="contained"
            color="error"
            startIcon={<RefreshIcon />}
          >
            {t('resetScorePopup.reset')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResetPopup;